/**
 * 店舗管理アプリのキー操作（バックオフィスでマウスを持たずに進められるようにする）。
 * plugins/keyboardOperation.js が document に付けるハンドラの中身。DOM だけを見る純粋な関数にして単体テストする。
 *
 *   Ctrl/⌘ + Enter（1行入力・テキストエリア・セレクト・候補選択の部品・フォーカスなしでも）:
 *     - [data-enter-scope] の中 → その中の [data-enter-default]
 *     - モーダルの中 → そのモーダルのフッターの主ボタン（.btn-primary が1つだけのとき）
 *     - フォーカスがモーダル外 → 一番上に開いているモーダル、無ければ設定画面の保存バー（.settings-save-bar）
 *   Enter 単体（1行の入力欄で）:
 *     - [data-enter-scope] の中だけ → [data-enter-default]（詳細フィルタの「適用」など、データを書き換えない検索）
 *     - フォームのモーダル・保存バーは Enter 単体では確定しない（英語入力の Enter で誤送信しないため。2026-09-29 ユーザー指示）
 *
 * 押さない場合:
 *   - IME 変換中（変換の確定の Enter）・Shift/Alt つき・ほかのハンドラが preventDefault 済み
 *   - ボタン・リンク上（ブラウザ標準の Enter / そのボタン自身に任せる）
 *   - 主ボタンが無効（入力エラー）・非表示・2つ以上で決められない
 *   - モーダルの中に置かれた別のモーダル部品（ImageFileInput のトリミングなど）のボタンは、外のモーダルの主ボタンとみなさない
 *   以下は Enter 単体だけ（Ctrl/⌘ + Enter は明示の操作なので押す）:
 *   - 候補を Enter で選ぶ部品（v-autocomplete / v-select / b-form-tags / role=combobox）
 *   - [data-enter-submit="off"] の中（画面側で Enter を使っている入力欄）
 */
import { isImeComposing } from '~/utils/imeComposing'

// Enter で送ってよい1行入力（checkbox / radio / file / button 類は除く）
const TEXT_INPUT_TYPES = [
  'text',
  'search',
  'email',
  'tel',
  'url',
  'number',
  'password',
  'date',
  'datetime-local',
  'month',
  'time',
  'week'
]

export const ENTER_EXCLUDE_SELECTOR = [
  '.v-autocomplete',
  '.v-select',
  '.v-combobox',
  '.b-form-tags',
  '[role="combobox"]',
  '[data-enter-submit="off"]',
  '[contenteditable="true"]'
].join(', ')

const FIELD_SELECTOR = [
  'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]):not([type="button"]):not([type="submit"]):not([disabled]):not([readonly])',
  'textarea:not([disabled]):not([readonly])',
  'select:not([disabled])'
].join(', ')

export function isTextInput(el) {
  if (!el || el.tagName !== 'INPUT') return false
  const type = (el.getAttribute('type') || 'text').toLowerCase()
  return TEXT_INPUT_TYPES.includes(type)
}

// Ctrl/⌘ + Enter を受ける欄（テキストエリア・セレクトも含む）
function isFormControl(el) {
  if (!el) return false
  return isTextInput(el) || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT'
}

// 隠れている要素の中か（v-show の style="display: none;"・hidden 属性・CSS クラスでの display: none）
function isHidden(el, root) {
  const win = el && el.ownerDocument && el.ownerDocument.defaultView
  let node = el
  while (node && node !== root) {
    if (node.hidden) return true
    if (node.style && node.style.display === 'none') return true
    if (win && win.getComputedStyle) {
      const style = win.getComputedStyle(node)
      if (style.display === 'none' || style.visibility === 'hidden') return true
    }
    node = node.parentElement
  }
  return false
}

// el がそのスコープ自身のものか。スコープの中に置かれた別のモーダル部品（ImageFileInput のトリミング等）の中身は除く
function belongsTo(el, scope) {
  const own = scope.closest ? scope.closest('.modal-content') : null
  return closest(el, '.modal-content') === own
}

export function isUsableButton(btn, root) {
  if (!btn) return false
  if (btn.disabled) return false
  if (btn.classList && btn.classList.contains('disabled')) return false
  if (btn.getAttribute && btn.getAttribute('aria-disabled') === 'true')
    return false
  return !isHidden(btn, root)
}

// スコープの既定ボタン。[data-enter-default] を優先し、モーダルならフッターの主ボタン（1つだけのとき）
export function findDefaultButton(scope) {
  if (!scope) return null
  const explicit = Array.from(
    scope.querySelectorAll('[data-enter-default]')
  ).filter(b => belongsTo(b, scope))
  if (explicit.length) {
    const usable = explicit.filter(b => isUsableButton(b, scope))
    return usable.length === 1 ? usable[0] : null
  }
  const footer = Array.from(
    scope.querySelectorAll('.modal-footer, .settings-save-bar')
  ).find(f => belongsTo(f, scope))
  const area =
    footer ||
    (scope.classList && scope.classList.contains('settings-save-bar')
      ? scope
      : null)
  if (!area) return null
  const primaries = Array.from(area.querySelectorAll('.btn-primary')).filter(
    b => belongsTo(b, scope) && !isHidden(b, area)
  )
  if (primaries.length !== 1) return null
  return isUsableButton(primaries[0], area) ? primaries[0] : null
}

function closest(el, selector) {
  return el && el.closest ? el.closest(selector) : null
}

// 一番上に開いているモーダル（フォーカスがモーダルの外にあるとき用）。
// 開いているモーダルの中に置かれた別のモーダル部品（.show でない .modal の中）は数えない
function topModalContent(doc) {
  const list = Array.from(
    doc.querySelectorAll('.modal.show .modal-content')
  ).filter(c => {
    const modal = closest(c, '.modal')
    return modal && modal.classList.contains('show')
  })
  return list.length ? list[list.length - 1] : null
}

/**
 * keydown から「押すボタン」を決める。押さないときは null。
 * @param {KeyboardEvent} event
 * @returns {HTMLElement|null}
 */
export function resolveEnterButton(event) {
  if (!event || event.key !== 'Enter') return null
  if (event.defaultPrevented || isImeComposing(event)) return null
  if (event.shiftKey || event.altKey) return null
  const target = event.target
  if (!target || !target.ownerDocument) return null

  const doc = target.ownerDocument
  const withModifier = event.ctrlKey || event.metaKey
  const scope = closest(target, '[data-enter-scope]')
  const modal = closest(target, '.modal-content')

  if (withModifier) {
    if (target.tagName === 'BUTTON' || target.tagName === 'A') return null
    if (scope) return findDefaultButton(scope)
    if (modal) return findDefaultButton(modal)
    // フォーカスがどこにも無い（body）ときも、開いているモーダルを優先する
    const top = topModalContent(doc)
    if (top) return findDefaultButton(top)
    const saveBar = doc.querySelector('.settings-save-bar')
    return saveBar ? findDefaultButton(saveBar) : null
  }

  // Enter 単体は検索欄（data-enter-scope）だけ。フォームの確定は Ctrl/⌘ + Enter
  if (!scope || !isTextInput(target)) return null
  if (closest(target, ENTER_EXCLUDE_SELECTOR)) return null
  return findDefaultButton(scope)
}

// 削除・破棄など取り消せない操作の確認か。最初のフォーカスを「キャンセル」に置く（Tab → Enter で確定）
// お金・予約・契約に関わる確認も含める（#614 回帰レビュー 2026-10-13 決定）。
// 払い戻=予約キャンセルの確認文、剥奪=チケット一部剥奪、再実行=決済の再実行（確認文に「決済」が無い）
const DESTRUCTIVE_WORDS =
  /削除|破棄|決済|返金|キャンセル|退会|入会|停止|払い戻|剥奪|取り消せません|再実行/
export function isDestructiveConfirm({
  message,
  okTitle,
  okVariant,
  color
} = {}) {
  if (okVariant === 'danger') return true
  if (color === 'danger' || color === 'error') return true
  return [message, okTitle].some(
    t => typeof t === 'string' && DESTRUCTIVE_WORDS.test(t)
  )
}

export function isFormField(el) {
  return !!el && !!el.matches && el.matches(FIELD_SELECTOR) && isFormControl(el)
}

/**
 * モーダルを開いたときに最初の入力欄へフォーカスする先。
 * すでに入力欄にフォーカスがある（autofocus 済み）・入力欄が無い・[data-no-autofocus] のモーダルは null。
 * @param {HTMLElement} modalContent .modal-content
 * @param {Element|null} active document.activeElement
 */
export function findInitialField(modalContent, active) {
  if (!modalContent) return null
  if (active && modalContent.contains(active) && isFormField(active))
    return null
  if (
    closest(modalContent, '[data-no-autofocus]') ||
    modalContent.querySelector('[data-no-autofocus]')
  )
    return null
  const body = modalContent.querySelector('.modal-body') || modalContent
  // 画面側で autofocus を指定した欄があればそれ（BootstrapVue の欄は autofocus 属性を出す）
  const auto = body.querySelector('[autofocus]')
  if (auto && isFormField(auto) && !isHidden(auto, body)) return auto
  const fields = Array.from(body.querySelectorAll(FIELD_SELECTOR))
  return (
    fields.find(
      f => !isHidden(f, body) && !closest(f, ENTER_EXCLUDE_SELECTOR)
    ) || null
  )
}
